import {CommonModule} from '@angular/common';
import {Component, computed, effect, input, output, viewChild, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SeFeEmptyStateModule} from 'se-fe-empty-state';
import {SeFeFormFieldOptionsModule} from 'se-fe-form-field-options';
import {SeFePaginationChange, SeFePaginationModule, SeFePaginationOptions} from 'se-fe-pagination';
import {SeFeSpinnerModule} from 'se-fe-spinner';
import {InitalSort, SeFeTableDataSource, SeFeTableModule, SeFeTableSortDirective, SortChangeEvent} from 'se-fe-table';
import {SeFeToolbarModule} from 'se-fe-toolbar';
import {ProductQueryParams, SortDirection} from '../../../models';
import {getProductId, Product} from '../../../models/clare';
import {ProductNameCellComponent} from '../product-table-cells/product-name-cell/product-name-cell.component';
import {ProductStatusCellComponent} from '../product-table-cells/product-status-cell/product-status-cell.component';

export interface ProductSelectionChange {
  currentPageProductIds: string[];
  selectedProducts: Product[];
}

@Component({
  selector: 'product-selection-table',
  imports: [
    CommonModule,
    ProductNameCellComponent,
    ProductStatusCellComponent,
    ReactiveFormsModule,
    SeFeEmptyStateModule,
    SeFeFormFieldOptionsModule,
    SeFePaginationModule,
    SeFeSpinnerModule,
    SeFeTableModule,
    SeFeToolbarModule,
  ],
  templateUrl: './product-selection-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../product-table/product-table.component.scss',
})
export class ProductSelectionTableComponent {
  queryChanged = output<ProductQueryParams>();
  selectionChanged = output<ProductSelectionChange>();

  sorter = viewChild.required(SeFeTableSortDirective);

  loading = input.required<boolean | null>();
  paginationEnabled = input(false);
  paginationOptions = input<SeFePaginationOptions>();
  products = input<Product[] | undefined>();
  query = input<string | undefined>();
  selectedProductIds = input<Set<string>>(new Set());
  showSearch = input(false);
  hideSearchWhenEmpty = input(false);
  sortBy = input<string | undefined>();
  sortDirection = input<SortDirection | undefined>();

  displayedColumns = ['Select', 'Name', 'Active'];
  form = new FormGroup<any>({
    selectAll: new FormControl(false),
  });
  selectAllOptions = [{label: 'Select all products', value: true, hiddenLabel: true}];
  rowSelectionOptions = [{label: 'Select product', value: true, hiddenLabel: true}];

  datasource = computed(() => new SeFeTableDataSource(this.products() || []));
  isZeroState = computed(() => !this.products()?.length && !this.query()?.trim());
  initialSort = computed((): InitalSort => ({
    column: this.sortBy() || '',
    direction: this.sortDirection() ?? null,
  }));

  private lastSelectAllValue = false;
  private syncingSelection = false;

  private get selectAllControl(): FormControl<boolean> {
    return this.form.get('selectAll') as FormControl<boolean>;
  }

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.handleFormChange());

    effect(() => {
      const products = this.products() || [];
      const selectedProductIds = this.selectedProductIds();
      this.syncForm(products, selectedProductIds);
    });
  }

  handleSearch(queryString: string): void {
    this.queryChanged.emit({
      page: 1,
      query: queryString.trim(),
    });
  }

  paginationChange(event: SeFePaginationChange): void {
    const queryParams: ProductQueryParams = {
      page: event.currentPage,
      take: event.limit,
    };

    try {
      const currentSorter = this.sorter();
      const currentSortColumn = currentSorter?.active;
      const currentSortDirection = currentSorter?.direction;

      if (currentSortColumn && currentSortDirection && currentSortDirection !== '') {
        queryParams.sortBy = currentSortColumn;
        queryParams.direction = currentSortDirection as SortDirection;
      }
    } catch {
      // Sorter can be unavailable while shallow tests render the component shell.
    }

    this.queryChanged.emit(queryParams);
  }

  sortChange(event: SortChangeEvent): void {
    if (!event || event.initial) return;

    const currentLimit = this.paginationOptions()?.limit || 10;
    const queryParams: ProductQueryParams = {
      page: 1,
      take: currentLimit,
      sortBy: event.direction ? event.column : undefined,
      direction: event.direction ? (event.direction as SortDirection) : undefined,
    };

    this.queryChanged.emit(queryParams);
  }

  getRowId(product: Product): string {
    return getProductId(product) || '';
  }

  private handleFormChange(): void {
    if (this.syncingSelection) return;

    const selectAllValue = this.selectAllControl.value;
    if (selectAllValue !== this.lastSelectAllValue) {
      this.setProductControlValues(selectAllValue);
    } else {
      this.syncSelectAllControl();
    }

    this.lastSelectAllValue = this.selectAllControl.value;
    this.selectionChanged.emit({
      currentPageProductIds: this.currentPageProductIds(),
      selectedProducts: this.selectedProductsFromForm(),
    });
  }

  private syncForm(products: Product[], selectedProductIds: Set<string>): void {
    this.syncingSelection = true;

    const productIds = products.map(product => this.getRowId(product));
    Object.keys(this.form.controls).forEach(controlName => {
      if (controlName !== 'selectAll' && !productIds.includes(controlName)) {
        this.form.removeControl(controlName, {emitEvent: false});
      }
    });

    productIds.forEach(productId => {
      if (!this.form.get(productId)) {
        this.form.addControl(productId, new FormControl(false), {emitEvent: false});
      }
      this.form.get(productId)?.setValue(selectedProductIds.has(productId), {emitEvent: false});
    });

    this.syncSelectAllControl();
    this.lastSelectAllValue = this.selectAllControl.value;
    this.syncingSelection = false;
  }

  private setProductControlValues(value: boolean): void {
    Object.keys(this.form.controls).forEach(controlName => {
      if (controlName !== 'selectAll') {
        this.form.get(controlName)?.setValue(value, {emitEvent: false});
      }
    });
  }

  private syncSelectAllControl(): void {
    const products = this.products() || [];
    const allSelected =
      products.length > 0 && products.every(product => this.form.get(this.getRowId(product))?.value === true);
    this.selectAllControl.setValue(allSelected, {emitEvent: false});
  }

  private selectedProductsFromForm(): Product[] {
    return (this.products() || []).filter(product => this.form.get(this.getRowId(product))?.value === true);
  }

  private currentPageProductIds(): string[] {
    return (this.products() || []).map(product => this.getRowId(product));
  }
}
